import React, { memo, useMemo } from "react";
import { Badge, Box, Button, Typography } from "@mui/material";
import moment from "moment";
import { msgEncrypter } from "../../../utils/messageEncrypt";
import PersonIcon from "../../../assets/images/chats/person.svg";
import group_icon from "../../../assets/images/chats/Group_empty.svg";
import buyer_seller_grp_icon from "../../../assets/images/chats/buyer_seller_grp_icon.svg";
import { colorSchemes, personIconStyle } from "../../../assets/commonFunction";
import { createStyledIndicatorButton } from "../../../common/StyledComponents/SideBarComponents";
import Archive from "../../../common/SvgImageView/Archive";
import UnArchive from "../../../common/SvgImageView/UnArchive";

// Made once: a styled component made while rendering is a new component
// each time, so every row's button was thrown away and built again on every
// render of the list.
const SmallActionButton = createStyledIndicatorButton("#FDFDFD");

const SELECTED_BACKGROUND = "#efefef";

// The last message's text: decrypted once per message, not on every render.
const useLastMessageText = (lastMessage) => useMemo(() => {
  if (!lastMessage?.message) return "";
  try {
    return lastMessage.kuncika
      ? msgEncrypter.decrypt(lastMessage.message, process.env.REACT_APP_ENC_KEY, lastMessage.kuncika)
      : lastMessage.message;
  } catch (e) {
    return "";
  }
}, [lastMessage?.message, lastMessage?.kuncika]);

// One group or contact in the side bar (the list and the archived list).
// actionMode: "archive" | "unarchive", the button shown when showAction.
const ChatListRow = ({
  item,
  isSelected,
  isSelf,
  showAction,
  actionMode,
  hideBadge,
  selfLabel,
  timeFormat,
  dateFormat,
  onBadgeClick,
  onItemClick,
  onActionClick,
}) => {
  const text = useLastMessageText(item?.lastMessage);
  const archive = actionMode === "archive";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        backgroundColor: isSelected ? SELECTED_BACKGROUND : "transparent",
        borderRadius: "0.1rem",
      }}
    >
      <Box
        width={"75%"}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.1rem",
          cursor: "pointer",
        }}
      >
        <Box width={"15%"}>
          <SmallActionButton
            size="small"
            variant="contained"
            sx={{
              padding: item?.accountDetails?.profileImgUrl && !item?.isGroup ? "0px" : "6px",
              overflow: "hidden",
              backgroundColor: isSelected ? SELECTED_BACKGROUND : "#FDFDFD",
              "&:hover": {
                backgroundColor: isSelected ? SELECTED_BACKGROUND : "#FDFDFD",
              },
            }}
            onClick={() => onBadgeClick(item)}
          >
            {item?.isGroup ? (
              <img
                style={{ width: "100%", height: "100%" }}
                src={item?.isBuyerSellerGroup ? buyer_seller_grp_icon : group_icon}
                alt="group"
              />
            ) : (
              <>
                {item?.accountDetails?.profileImgUrl ? (
                  <img
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    src={item?.accountDetails?.profileImgUrl}
                    alt=""
                    loading="lazy"
                  />
                ) : (
                  <img style={personIconStyle} src={PersonIcon} alt="individual" />
                )}
              </>
            )}
          </SmallActionButton>
        </Box>

        <Box
          width={"84%"}
          padding={"0.2rem 0.4rem"}
          borderRadius="0.1rem"
          cursor="pointer"
          onClick={() => onItemClick(item)}
          position={"relative"}
        >
          <Box display={"flex"} alignItems={"center"}>
            <Typography
              maxWidth={isSelf ? "60%" : "80%"}
              fontSize="13px"
              fontWeight="400"
              color={"black"}
              variant="h6"
              title={item?.name}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item?.name}
            </Typography>
            {isSelf && (
              <Typography ml={"0.1rem"} variant="caption">
                ({selfLabel})
              </Typography>
            )}
          </Box>
          {text ? (
            <Typography
              fontSize="11px"
              color="#666"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {item?.lastMessage?.from?.name ? `${item.lastMessage.from.name}: ` : ""}
              {text}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {showAction ? (
        <Box padding={"0rem 0.1rem 0rem 0rem"} width={"25%"} textAlign={"right"}>
          <Button
            onClick={() => onActionClick(item)}
            size="small"
            variant="contained"
            sx={{
              padding: "0px",
              ...(archive && { border: `0.5px solid ${colorSchemes.black}` }),
              backgroundColor: `${colorSchemes.white}`, // always white
              width: "1.87rem",
              height: "1.87rem",
              minWidth: "unset",
              minHeight: "unset",
              borderRadius: "0.15rem",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: `${colorSchemes.white}`, // no blue on hover
                boxShadow: "none",
              },
            }}
          >
            {archive ? <Archive color={"#000000"} /> : <UnArchive color={"#000000"} />}{" "}
            {/* always black */}
          </Button>
        </Box>
      ) : (
        (item?.timetoken !== 0 && item?.timetoken) || (item?.messageCount !== 0 && !hideBadge)
          ? (
            <Box
              padding={"0rem 0.1rem 0rem 0rem"}
              width={"25%"}
              display={"flex"}
              flexDirection={"column"}
              alignItems={"flex-end"}
              gap={"0.2rem"}
            >
              {item?.timetoken !== 0 && item?.timetoken && (
                <Typography variant="h6" fontSize={"0.74rem"} fontWeight={500} color={"#183243"}>
                  {(() => {
                    const date = moment(Math.floor(item.timetoken / 10000));
                    return date.isSame(moment(), "day") ? date.format(timeFormat) : date.format(dateFormat);
                  })()}
                </Typography>
              )}
              {item?.messageCount !== 0 && !hideBadge && (
                <Badge
                  color="success"
                  badgeContent={item?.messageCount}
                  max={20}
                  sx={{
                    "& .MuiBadge-badge": {
                      position: "static",
                      transform: "none",
                      backgroundColor: "#183243",
                      fontSize: "0.7rem",
                    },
                  }}
                />
              )}
            </Box>
          ) : null
      )}
    </div>
  );
};

export default memo(ChatListRow);
